
import { errorHandlingFetcher, RedirectError } from "@/shared/lib/fetcher";
import { useSWRCompat as useSWR } from "@/shared/lib/queryClient";
import { SWR_KEYS } from "@/shared/lib/swr-keys";
import Modal from "@/shared/ui/refresh-components/Modal";
import { useCallback, useEffect, useRef, useState } from "react";
import { getSecondsUntilExpiration } from "@/shared/lib/time";
import { refreshToken } from "@/shared/lib/user";
import { VITE_CUSTOM_REFRESH_URL } from "@/shared/lib/constants";
import { Button } from "@opal/components";
import { logout } from "@/shared/lib/user";
import { useLocation, useNavigate } from "react-router-dom";
import { SvgAlertTriangle, SvgLogOut } from "@opal/icons";
import { Content } from "@opal/layouts";
import { useCurrentUser } from "@/shared/hooks/useCurrentUser";
import { getExtensionContext } from "@/shared/lib/extension/utils";

export default function AppHealthBanner() {
  const navigate = useNavigate();
  const { error } = useSWR(SWR_KEYS.health, errorHandlingFetcher);
  const [expired, setExpired] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { pathname } = useLocation();
  const expirationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timer | null>(null);
  // Latches true once we see an authed user — separates mid-session logout
  // from a fresh unauth load.
  const hasSeenAuthenticatedUserRef = useRef(false);

  const { user, mutateUser, userError } = useCurrentUser();
  if (user) {
    hasSeenAuthenticatedUserRef.current = true;
  }

  const isAuthPage = pathname?.startsWith("/auth") ?? false;
  const sessionEnded =
    userError?.status === 403 || error instanceof RedirectError || expired;
  const showLoggedOutModal =
    !dismissed &&
    sessionEnded &&
    hasSeenAuthenticatedUserRef.current &&
    !isAuthPage;

  // Clear the server session on 403 for a previously-authed user.
  useEffect(() => {
    if (userError?.status === 403 && hasSeenAuthenticatedUserRef.current) {
      logout();
    }
  }, [userError]);

  function handleLogin() {
    setDismissed(true);
    const { isExtension } = getExtensionContext();
    if (isExtension) {
      // In the Chrome extension, open login in a new tab so OAuth popups
      // work correctly (the extension iframe has no navigable URL origin).
      window.open(
        window.location.origin + "/auth/login",
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      navigate("/auth/login");
    }
  }

  const setupExpirationTimeout = useCallback(
    (secondsUntilExpiration: number) => {
      if (expirationTimeoutRef.current) {
        clearTimeout(expirationTimeoutRef.current);
      }

      const timeUntilExpire = (secondsUntilExpiration + 10) * 1000;
      expirationTimeoutRef.current = setTimeout(() => {
        setExpired(true);
      }, timeUntilExpire);
    },
    []
  );

  // Clean up any timeouts/intervals when component unmounts
  useEffect(() => {
    return () => {
      if (expirationTimeoutRef.current) {
        clearTimeout(expirationTimeoutRef.current);
      }

      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // Set up token refresh logic if custom refresh URL exists
  useEffect(() => {
    if (!user) return;

    const secondsUntilExpiration = getSecondsUntilExpiration(user);
    if (secondsUntilExpiration === null) return;

    // Set up expiration timeout based on current user data
    setupExpirationTimeout(secondsUntilExpiration);

    if (VITE_CUSTOM_REFRESH_URL) {
      const refreshUrl = VITE_CUSTOM_REFRESH_URL;

      const attemptTokenRefresh = async () => {
        let retryCount = 0;
        const maxRetries = 3;

        while (retryCount < maxRetries) {
          try {
            const refreshTokenData = await refreshToken(refreshUrl);
            if (!refreshTokenData) {
              throw new Error("Failed to refresh token");
            }

            const response = await fetch(
              "/api/enterprise-settings/refresh-token",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(refreshTokenData),
              }
            );
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Wait for backend to process the token
            await new Promise((resolve) => setTimeout(resolve, 4000));

            // Get updated user data
            const updatedUser = await mutateUser();

            if (updatedUser) {
              // Reset expiration timeout with new expiration time
              const newSecondsUntilExpiration =
                getSecondsUntilExpiration(updatedUser);
              if (newSecondsUntilExpiration !== null) {
                setupExpirationTimeout(newSecondsUntilExpiration);
                console.debug(
                  `Token refreshed, new expiration in ${newSecondsUntilExpiration} seconds`
                );
              }
            }

            break; // Success - exit the retry loop
          } catch (error) {
            console.error(
              `Error refreshing token (attempt ${
                retryCount + 1
              }/${maxRetries}):`,
              error
            );
            retryCount++;

            if (retryCount === maxRetries) {
              console.error("Max retry attempts reached");
            } else {
              // Wait before retrying (exponential backoff)
              await new Promise((resolve) =>
                setTimeout(resolve, Math.pow(2, retryCount) * 1000)
              );
            }
          }
        }
      };

      // Set up refresh interval
      const refreshInterval = 60 * 15; // 15 mins

      // Clear any existing interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }

      refreshIntervalRef.current = setInterval(
        attemptTokenRefresh,
        refreshInterval * 1000
      );

      // If we're going to expire before the next refresh, kick off a refresh now
      if (secondsUntilExpiration < refreshInterval) {
        attemptTokenRefresh();
      }
    }
  }, [user, setupExpirationTimeout, mutateUser]);

  if (showLoggedOutModal) {
    return (
      <Modal open>
        <Modal.Content width="sm" height="sm">
          <Modal.Header icon={SvgLogOut} title="You Have Been Logged Out" />
          <Modal.Body>
            <p className="text-sm">
              Your session has expired. Please log in again to continue.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={handleLogin}>Log In</Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    );
  }

  if (!error && !expired) {
    return null;
  }

  if (error instanceof RedirectError || expired) {
    return null;
  } else {
    return (
      <div className="w-full bg-status-error-01 p-3">
        <Content
          icon={SvgAlertTriangle}
          title="The backend is currently unavailable"
          description="If this is your initial setup or you just updated your VNP Workplace deployment, this is likely because the backend is still starting up. Give it a minute or two, and then refresh the page. If that does not work, make sure the backend is setup and/or contact an administrator."
          sizePreset="main-content"
          variant="section"
        />
      </div>
    );
  }
}
